import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@env/insitestore360';
import { DeleteDialogComponent } from '@insite-store360/UI';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  /* Used for header text dynamic based on tab */
  public headerData = new Subject();
  /** Optional last breadcrumb label (e.g. store name on Store 360). */
  private readonly breadcrumbDetailSubject = new BehaviorSubject<string | null>(null);
  readonly breadcrumbDetail$ = this.breadcrumbDetailSubject.asObservable();

  constructor(private dialog: MatDialog, private toastr: ToastrService) { }
  /* Method is used for updating header data */
  public headerValue(title: string) {
    this.headerData.next({
      title: title,
    });
  }

  /** Set/clear dynamic breadcrumb leaf (store name, etc.). */
  public setBreadcrumbDetail(title: string | null): void {
    const next = title?.trim() || null;
    if (this.breadcrumbDetailSubject.value === next) {
      return;
    }
    this.breadcrumbDetailSubject.next(next);
  }

  public getBreadcrumbDetail(): string | null {
    return this.breadcrumbDetailSubject.value;
  }

  public allowOnlyNumber(event: KeyboardEvent): Boolean {
    const charCode = event.which ? event.which : event.keyCode;

    // Allow only numbers (48-57 for 0-9)
    if (charCode < 48 || charCode > 57) {
      event.preventDefault(); // Prevent other characters from being typed
      return false;
    }
    return true;
  }

  public allowOnlyLetters(event: KeyboardEvent) {
    const charCode = event.charCode;

    // Allow uppercase (A-Z), lowercase (a-z), and space (charCode 32)
    if (
      (charCode >= 65 && charCode <= 90) || // A-Z
      (charCode >= 97 && charCode <= 122) || // a-z
      charCode === 32 // space
    ) {
      return true; // Allow the character
    } else {
      event.preventDefault(); // Block the character
      return false;
    }
  }

  public allowOnlyLettersAndNumbers(event: KeyboardEvent) {
    const charCode = event.charCode || event.keyCode; // Fallback for keyCode in older browsers

    // Allow uppercase (A-Z), lowercase (a-z), numbers (0-9), and space (charCode 32)
    if (
      (charCode >= 65 && charCode <= 90) || // A-Z
      (charCode >= 97 && charCode <= 122) || // a-z
      (charCode >= 48 && charCode <= 57) || // 0-9
      charCode === 32 // space
    ) {
      return true; // Allow the character
    } else {
      event.preventDefault(); // Block the character
      return false;
    }
  }

  public sanitizeInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.value = inputElement.value
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase();
  }

  deleteItem(
    itemName: string,
    deleteCallback: () => Observable<any>,
    refreshCallback: () => void
  ) {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      disableClose: true,
      height: 'auto',
      width: '350px',
      data: itemName,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'confirm') {
        deleteCallback().subscribe(
          () => {
            this.toastr.success('Deleted successfully');
            refreshCallback();
          },
          (err) => {
            // Prefer the specific message returned by the backend
            // (e.g. TDS/TDL mapped to promoters cannot be deleted until remapped).
            const raw =
              (err instanceof Error ? err.message : null) ||
              err?.error?.message ||
              err?.message ||
              'Failed to delete data';
            const message = Array.isArray(raw)
              ? raw.filter(Boolean).join(', ')
              : String(raw || '').trim();
            this.toastr.error(
              !message || message === 'undefined'
                ? 'Failed to delete data'
                : message
            );
          }
        );
      }
    });
  }
  public allowOnlyUppercase(event: KeyboardEvent): void {
    const inputElement = event.target as HTMLInputElement;
    let currentValue = inputElement.value;
    if (event.key >= 'a' && event.key <= 'z') {
      event.preventDefault();
      currentValue += event.key.toUpperCase();
    }
    else if (!/^[A-Z]$/.test(event.key) && event.key !== 'Backspace') {
      event.preventDefault();
    }
    inputElement.value = currentValue;
  }

  //timeAgo 
  public timeAgo(value: any) {
    const currentDate = new Date();
    const createdAtDate = new Date(value);

    const diffInMilliseconds = currentDate.getTime() - createdAtDate.getTime();
    const diffInSeconds = Math.floor(diffInMilliseconds / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} seconds ago`;
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hours ago`;
    } else if (diffInDays < 30) {
      return `${diffInDays} days ago`;
    } else if (diffInMonths < 12) {
      return `${diffInMonths} months ago`;
    } else {
      return `${diffInYears} years ago`;
    }
  }

  /** Browser FCM token for web push. Returns null when permission is denied or unavailable. */
  public async requestPermission(): Promise<string | null> {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return null;
    }
    if (Notification.permission === 'denied') {
      return null;
    }
    try {
      const { getApps, initializeApp } = await import('firebase/app');
      const { getMessaging, getToken } = await import('firebase/messaging');
      if (!getApps().length) {
        initializeApp(environment.firebaseConfig);
      }
      const currentToken = await getToken(getMessaging(), {
        vapidKey: environment.firebaseConfig.vapidKey,
      });
      return currentToken || null;
    } catch (err) {
      console.log('Unable to get web notification token', err);
      return null;
    }
  }

  // Add this helper method to get first day of current month
  public getFirstDayOfCurrentMonth(): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }

  // // Below this service purpose From the dialog box (Download Template), the selected user type options need to be used in the Master User component.
  // private selectedOptionSubject = new BehaviorSubject<any>(null); // Default value is null
  // selectedOption$: Observable<any> = this.selectedOptionSubject.asObservable();

  // setSelectedOption(option: any) {
  //   this.selectedOptionSubject.next(option);
  // }

  public getFirstDayOfThreeMonthsBack(): Date {
    const today = new Date();

    // Clone date
    const start = new Date(today.getFullYear(), today.getMonth(), 1);

    // Move 3 months backward
    start.setMonth(start.getMonth() - 3);

    return start;
  }

  public hasAccess(): boolean {
    // debugger;
    const username = localStorage.getItem('username');
    return !!username && (environment as any).accessPerson?.includes(username);
  }

}
